import {Appearance, randomAppearance} from './appearance';

export type SpellRef = {id: string; title: string; originalTitle: string; level: number};

export type SpellbookCircle = {level: number; spells: SpellRef[]};

export type Spellbook = {
	maxLevel: number;
	appearance: Appearance;
	circles: SpellbookCircle[];
	blankPrice: number;
	copyTotal: number;
	total: number;
};

export const MAX_LEVEL = 9;
export const BLANK_BOOK_PRICE = 50;
export const FIRST_LEVEL_SPELLS = 6;
export const MIN_SPELLS_PER_LEVEL = 2;

export const COPY_PRICE: Record<number, number> = {
	1: 25,
	2: 75,
	3: 150,
	4: 300,
	5: 750,
	6: 5000,
	7: 6250,
	8: 7500,
	9: 25000,
};

export const COUNT_SPREAD = 0.2;

export function spellCounts(maxLevel: number): number[] {
	const level = Math.min(Math.max(Math.round(maxLevel), 1), MAX_LEVEL);
	const total = 4 * level + 2;
	const counts = [FIRST_LEVEL_SPELLS];
	if (level === 1) return counts;

	const higher = level - 1;
	const rest = total - FIRST_LEVEL_SPELLS - MIN_SPELLS_PER_LEVEL * higher;
	const weights = Array.from({length: higher}, (_, index) => higher - index + 1);
	const sum = weights.reduce((a, b) => a + b, 0);
	const shares = weights.map((weight) => (rest * weight) / sum);
	const extras = shares.map(Math.floor);
	let left = rest - extras.reduce((a, b) => a + b, 0);

	const byFraction = shares.map((share, index) => ({index, fraction: share - Math.floor(share)})).sort((a, b) => b.fraction - a.fraction);
	for (const {index} of byFraction) {
		if (left <= 0) break;
		extras[index] += 1;
		left -= 1;
	}
	return counts.concat(extras.map((extra) => MIN_SPELLS_PER_LEVEL + extra));
}

export function shuffle<T>(items: T[], random: () => number = Math.random): T[] {
	const result = [...items];
	for (let index = result.length - 1; index > 0; index--) {
		const other = Math.floor(random() * (index + 1));
		[result[index], result[other]] = [result[other], result[index]];
	}
	return result;
}

export function generateSpellbook(pool: SpellRef[], maxLevel: number, random: () => number = Math.random): Spellbook {
	const factor = 1 - COUNT_SPREAD + random() * 2 * COUNT_SPREAD;
	const counts = spellCounts(maxLevel).map((count) => Math.max(1, Math.round(count * factor)));
	const circles = counts.map((count, index) => {
		const level = index + 1;
		const spells = shuffle(
			pool.filter((spell) => spell.level === level),
			random,
		)
			.slice(0, count)
			.sort((a, b) => a.title.localeCompare(b.title, 'ru'));
		return {level, spells};
	});
	const copyTotal = circles.reduce((sum, circle) => sum + circle.spells.length * COPY_PRICE[circle.level], 0);
	return {maxLevel: counts.length, appearance: randomAppearance(random), circles, blankPrice: BLANK_BOOK_PRICE, copyTotal, total: BLANK_BOOK_PRICE + copyTotal};
}
